// components/editor/use-apply-cart.ts
import { useState } from "react"
import { dispatchCart, type CartItem } from "./edit-cart"
import { readToolbarToken } from "@/lib/edit/toolbar-token"

export interface ApplyResult {
  msg: string
  prUrl?: string
  ok: boolean
}

/** Shown when the SSO session has aged out — actionable, and reassures that the
 *  cart is safe (it's persisted in localStorage, only cleared on success). This
 *  is the friendly face of the server's cryptic "Invalid service account" 403
 *  (AHD-8021): the Firebase idToken the toolbar captured at mount expired (~1h). */
const SESSION_EXPIRED_MSG =
  "Session expired — refresh the page to re-authenticate, then Apply again. Your staged changes are saved."

/**
 * Resolve the freshest token to send. The `idToken` handed down from the
 * ahead-core toolbar is captured once on mount and never refreshed, so on a
 * long-open tab it's stale. The bridge persists the live token first-party
 * (same `readToolbarToken` source, with an explicit `expiresAt`), so prefer
 * that when it's still fresh; if it's present but expired, don't fire a doomed
 * request. Dev-impersonation tokens (`dev:<email>`) never expire — pass through.
 */
function resolveSendToken(
  idToken: string
): { token: string } | { expired: true } {
  if (idToken.startsWith("dev:")) return { token: idToken }
  const stored = readToolbarToken()
  if (stored) {
    return stored.expiresAt - Date.now() > 60_000
      ? { token: stored.idToken }
      : { expired: true }
  }
  // No persisted token to check freshness against — trust the passed token and
  // let the server verify (a 403 there maps back to SESSION_EXPIRED_MSG too).
  return { token: idToken }
}

/**
 * Shared PR/CMS apply machinery extracted verbatim from CockpitCart so both the
 * desktop cart and MobilePendingBar drive the exact same write path. Apply POSTs
 * to /api/edit/pr + /api/edit/cms with the SSO idToken in a `User-Authorization`
 * header — verified server-side by ahead-core's authorizeSystemAccess. Clears
 * the cart on full success; keeps it on any error.
 */
export function useApplyCart({
  idToken,
  currentRef,
  env,
}: {
  idToken: string
  currentRef: string
  env: string
}) {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ApplyResult | null>(null)

  // One POST with the given token + a 30s timeout. Returns the parsed body (plus
  // the HTTP status so callers can special-case a 403) or a synthetic error
  // object — never throws, so callers can combine results.
  async function postEdit(
    path: string,
    body: unknown,
    token: string
  ): Promise<{
    ok: boolean
    url?: string
    branch?: string
    drafts?: number
    error?: string
    httpStatus?: number
  }> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), 30000)
    try {
      const res = await fetch(path, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "User-Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      })
      const parsed = await res
        .json()
        .catch(() => ({ ok: false, error: `HTTP ${res.status}` }))
      return { ...parsed, httpStatus: res.status }
    } catch (e) {
      return {
        ok: false,
        error:
          e instanceof DOMException && e.name === "AbortError"
            ? "timed out after 30s"
            : `request failed: ${e instanceof Error ? e.message : String(e)}`,
      }
    } finally {
      clearTimeout(timer)
    }
  }

  async function apply(intlItems: CartItem[], cmsItems: CartItem[]) {
    setBusy(true)
    setResult(null)

    // Preflight the session before touching GitHub/CMS. An aged-out token would
    // 403 as "Invalid service account" server-side; catch it here and show the
    // actionable message instead, leaving the cart intact.
    const resolved = resolveSendToken(idToken)
    if ("expired" in resolved) {
      const out: ApplyResult = { msg: SESSION_EXPIRED_MSG, ok: false }
      setResult(out)
      setBusy(false)
      return out
    }
    const token = resolved.token

    const [prRes, cmsRes] = await Promise.all([
      intlItems.length
        ? postEdit("/api/edit/pr", { items: intlItems, currentRef, env }, token)
        : null,
      cmsItems.length
        ? postEdit("/api/edit/cms", { items: cmsItems }, token)
        : null,
    ])

    // A 403 from either route means the token was rejected server-side (verified
    // expiry / revocation the client clock couldn't see) — surface the same
    // re-auth guidance rather than the raw "Invalid service account".
    if (prRes?.httpStatus === 403 || cmsRes?.httpStatus === 403) {
      const out: ApplyResult = { msg: SESSION_EXPIRED_MSG, ok: false }
      setResult(out)
      setBusy(false)
      return out
    }

    const parts: string[] = []
    let prUrl: string | undefined
    let allOk = true
    if (prRes) {
      if (prRes.ok) {
        prUrl = prRes.url
        parts.push(prRes.url ? "PR ready" : `committed to ${prRes.branch}`)
      } else {
        allOk = false
        parts.push(`PR error: ${prRes.error}`)
      }
    }
    if (cmsRes) {
      if (cmsRes.ok) parts.push(`${cmsRes.drafts} CMS draft(s) created`)
      else {
        allOk = false
        parts.push(`CMS error: ${cmsRes.error}`)
      }
    }
    const out: ApplyResult = { msg: parts.join(" · "), prUrl, ok: allOk }
    setResult(out)
    if (allOk) dispatchCart({ type: "clear" })
    setBusy(false)
    return out
  }

  return { busy, result, setResult, apply }
}
